import { useEffect, useLayoutEffect, useRef, useState } from "react";

interface ExpandableTextProps {
  text: string;
  lines?: number;
  className?: string;
}

export function ExpandableText({
  text,
  lines = 2,
  className = "",
}: ExpandableTextProps) {
  const textRef = useRef<HTMLParagraphElement | null>(null);

  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState<number>(0);
  const [fullHeight, setFullHeight] = useState<number>(0);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const compute = () => {
      const computed = window.getComputedStyle(el);
      const lineHeight = parseFloat(computed.lineHeight);

      if (!lineHeight) return;

      const targetCollapsedHeight = Math.round(lineHeight * lines);
      setCollapsedHeight(targetCollapsedHeight);

      const prevMaxHeight = el.style.maxHeight;
      const prevWebkitLineClamp = (el.style as any).webkitLineClamp;
      const prevOverflow = el.style.overflow;
      const prevDisplay = el.style.display;
      const prevWebkitBoxOrient = (el.style as any).webkitBoxOrient;

      el.style.maxHeight = "none";
      el.style.overflow = "visible";
      el.style.display = "block";
      (el.style as any).webkitLineClamp = "unset";
      (el.style as any).webkitBoxOrient = "unset";

      const measuredFullHeight = el.scrollHeight;
      setFullHeight(measuredFullHeight);
      setCanExpand(measuredFullHeight > targetCollapsedHeight + 2);

      el.style.maxHeight = prevMaxHeight;
      el.style.overflow = prevOverflow;
      el.style.display = prevDisplay;
      (el.style as any).webkitLineClamp = prevWebkitLineClamp;
      (el.style as any).webkitBoxOrient = prevWebkitBoxOrient;
    };

    compute();

    const ro = new ResizeObserver(() => compute());
    ro.observe(el);

    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [text, lines]);

  useEffect(() => {
    if (!canExpand) {
      setExpanded(false);
    }
  }, [canExpand]);

  return (
    <div>
      <div
        className="overflow-hidden transition-[max-height] duration-300 ease-in-out"
        style={{
          maxHeight: expanded ? `${fullHeight}px` : `${collapsedHeight}px`,
        }}
      >
        <p
          ref={textRef}
          className={`text-sm leading-6 text-[#5d6b63] whitespace-pre-line ${className}`}
        >
          {text}
        </p>
      </div>

      {canExpand && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 inline-flex items-center text-sm font-semibold text-[#1f6b3d] hover:underline"
        >
          {expanded ? "Thu gọn" : "Xem thêm"}
        </button>
      )}
    </div>
  );
}